import { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import './ProductImportModal.css';

export default function ProductImportModal({ companyId, onClose, onImport }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [error, setError] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const MAX_SELECT = 5;

  const extractProduct = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/products/extract-from-url', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ url })
      });
      
      const result = await res.json();
      if (!result.success) {
        setError(result.message);
        return;
      }
      
      setExtracted(result.data);
      setSelectedImages(result.data.imagens.slice(0, Math.min(3, result.data.imagens.length)));
    } catch (err) {
      setError('Erro ao extrair produto');
    } finally {
      setLoading(false);
    }
  };

  const toggleImage = (img) => {
    setSelectedImages(prev => {
      if (prev.includes(img)) {
        return prev.filter(i => i !== img);
      }
      if (prev.length >= MAX_SELECT) {
        return prev;
      }
      return [...prev, img];
    });
  };

  const handleImport = async () => {
    if (selectedImages.length === 0) return;
    
    setDownloadingImages(true);
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      // Chamar rota para download e upload das imagens
      const res = await fetch('/api/products/download-images', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ imageUrls: selectedImages })
      });
      
      const result = await res.json();
      
      if (!result.success || result.urls.length === 0) {
        setError('Erro ao processar imagens: ' + (result.errors?.join(', ') || 'Falha no download'));
        setDownloadingImages(false);
        return;
      }
      
      // Usar URLs do Supabase retornadas pelo backend
      onImport({
        ...extracted,
        imagens: result.urls,
        imagem_principal: result.urls[0] || ''
      });
      onClose();
    } catch (err) {
      setError('Erro ao baixar imagens: ' + err.message);
      setDownloadingImages(false);
    }
  };

  if (extracted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content import-preview" onClick={e => e.stopPropagation()}>
          <h3>Preview do Produto</h3>
          <p className="subtitle">Revise e edite os dados antes de importar</p>
          <div className="form-group">
            <label>Nome do Produto *</label>
            <input 
              value={extracted.nome} 
              onChange={e => setExtracted({...extracted, nome: e.target.value})} 
              placeholder="Nome do produto"
            />
          </div>
          <div className="form-group">
            <label>Preço de Venda</label>
            <input 
              type="number" 
              step="0.01"
              value={extracted.preco || ''} 
              onChange={e => setExtracted({...extracted, preco: parseFloat(e.target.value) || ''})} 
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>
              Descrição Curta
              <small>(usada em listagens, máx. 200 caracteres)</small>
            </label>
            <textarea 
              value={extracted.descricao_curta} 
              onChange={e => setExtracted({...extracted, descricao_curta: e.target.value})}
              placeholder="Breve descrição para listagens"
              maxLength="200"
              rows="2"
            />
            <small className="char-count">{extracted.descricao_curta?.length || 0}/200</small>
          </div>
          
          <div className="form-group">
            <label>
              Descrição Completa
              <small>(detalhes completos do produto)</small>
            </label>
            <textarea 
              value={extracted.descricao} 
              onChange={e => setExtracted({...extracted, descricao: e.target.value})}
              placeholder="Descrição detalhada do produto..."
              rows="4"
            />
          </div>
          <div className="images-section">
            <label>
              Imagens do Produto
              <small>{extracted.imagens.length} encontradas | {selectedImages.length}/{MAX_SELECT} selecionadas</small>
            </label>
            <div className="images-grid">
              {extracted.imagens.map((img, i) => (
                <div 
                  key={i} 
                  className={`image-item ${selectedImages.includes(img) ? 'selected' : ''} ${selectedImages.indexOf(img) === 0 ? 'principal' : ''}`}
                  onClick={() => toggleImage(img)}
                >
                  <img src={img} alt={`Imagem ${i + 1}`} loading="lazy" />
                  {selectedImages.includes(img) && (
                    <div className="selection-indicator">
                      {selectedImages.indexOf(img) === 0 ? '★ Principal' : `✓ ${selectedImages.indexOf(img) + 1}`}
                    </div>
                  )}
                  {!selectedImages.includes(img) && selectedImages.length >= MAX_SELECT && (
                    <div className="selection-disabled">Limite atingido</div>
                  )}
                </div>
              ))}
            </div>
            <p className="images-hint">
              Clique nas imagens para selecionar. A primeira selecionada será a imagem principal.
            </p>
          </div>
          <div className="modal-actions">
            <button onClick={onClose} className="btn-secondary">Cancelar</button>
            <button onClick={handleImport} className="btn-primary" disabled={selectedImages.length === 0 || downloadingImages}>
              {downloadingImages ? 'Processando imagens...' : 'Importar Produto'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>Importar Produto via URL</h3>
        <p className="subtitle">Custo: 1 crédito por extração • Extrai até 10 imagens</p>
        <div className="form-group">
          <label>URL do Produto</label>
          <input 
            type="url" 
            placeholder="https://seusite.com/produto" 
            value={url}
            onChange={e => setUrl(e.target.value)}
          />
        </div>
        {error && <p className="error-message">{error}</p>}
        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={extractProduct} disabled={loading || !url.trim()} className="btn-primary">
            {loading ? 'Extraindo...' : 'Extrair Produto'}
          </button>
        </div>
      </div>
    </div>
  );
}
